import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { toast } from "sonner";
import { CalendarDays } from "lucide-react";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import api, { apiErrorMessage } from "@/lib/api";
import { ACTIVITIES, ACCOMMODATION_TYPES } from "@/lib/constants";

function DateField({ label, testid, value, onChange, disabled }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            data-testid={testid}
            type="button"
            className="w-full min-h-[56px] rounded-xl border border-[#333333] bg-[#1A1A1A] px-4 flex items-center justify-between text-left"
          >
            <span className={value ? "text-[#F8FAFC]" : "text-[#666666]"}>
              {value ? format(value, "PPP") : "Select date"}
            </span>
            <CalendarDays size={20} className="text-[#666666]" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={value}
            onSelect={(d) => {
              onChange(d);
              setOpen(false);
            }}
            disabled={disabled}
            initialFocus
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}

export default function NewTrip() {
  const navigate = useNavigate();
  const [cities, setCities] = useState([]);
  const [city, setCity] = useState("");
  const [start, setStart] = useState(null);
  const [end, setEnd] = useState(null);
  const [accommodation, setAccommodation] = useState("");
  const [activities, setActivities] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/cities").then(({ data }) => setCities(data));
  }, []);

  const selectedCity = cities.find((c) => c.city === city);

  const toggleActivity = (a) =>
    setActivities((p) => (p.includes(a) ? p.filter((x) => x !== a) : [...p, a]));

  const submit = async () => {
    if (!city || !start || !end || !accommodation) {
      toast.error("Please fill in destination, dates and accommodation.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/trips", {
        destination_city: selectedCity.city,
        destination_country: selectedCity.country,
        start_date: format(start, "yyyy-MM-dd"),
        end_date: format(end, "yyyy-MM-dd"),
        accommodation_type: accommodation,
        planned_activities: activities,
      });
      navigate(`/trips/${data.id}/report`);
    } catch (e) {
      toast.error(apiErrorMessage(e));
      setSubmitting(false);
    }
  };

  return (
    <AppShell active="trips" title="New trip" showBack>
      <div className="space-y-6">
        <div className="space-y-2">
          <Label>Destination</Label>
          <Select value={city} onValueChange={setCity}>
            <SelectTrigger data-testid="destination-select" className="min-h-[56px] rounded-xl bg-[#1A1A1A] text-base">
              <SelectValue placeholder="Choose a city" />
            </SelectTrigger>
            <SelectContent className="max-h-[300px]">
              {cities.map((c) => (
                <SelectItem key={c.city} value={c.city}>
                  {c.city}, {c.country}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {selectedCity && (
            <p className="text-sm text-[#A1A1AA]">Country: {selectedCity.country}</p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <DateField label="Start date" testid="start-date" value={start} onChange={setStart} />
          <DateField
            label="End date"
            testid="end-date"
            value={end}
            onChange={setEnd}
            disabled={(d) => (start ? d < start : false)}
          />
        </div>

        <div className="space-y-2">
          <Label>Accommodation type</Label>
          <Select value={accommodation} onValueChange={setAccommodation}>
            <SelectTrigger data-testid="accommodation-select" className="min-h-[56px] rounded-xl bg-[#1A1A1A] text-base">
              <SelectValue placeholder="Choose accommodation" />
            </SelectTrigger>
            <SelectContent>
              {ACCOMMODATION_TYPES.map((a) => (
                <SelectItem key={a} value={a}>
                  {a}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>Planned activities</Label>
          <div className="flex flex-wrap gap-2">
            {ACTIVITIES.map((a) => (
              <button
                key={a}
                type="button"
                data-testid={`activity-${a}`}
                aria-pressed={activities.includes(a)}
                onClick={() => toggleActivity(a)}
                className={`min-h-[44px] px-4 rounded-full border-2 capitalize font-medium transition-colors ${
                  activities.includes(a)
                    ? "border-[#EF9F27] bg-[#EF9F27]/15 text-[#F8FAFC]"
                    : "border-[#333333] bg-[#1A1A1A] text-[#A1A1AA]"
                }`}
              >
                {a}
              </button>
            ))}
          </div>
        </div>

        <Button
          data-testid="generate-report-btn"
          onClick={submit}
          disabled={submitting}
          className="w-full min-h-[56px] rounded-xl bg-[#EF9F27] hover:bg-[#EF9F27] text-white text-lg font-semibold"
        >
          {submitting ? "Creating trip…" : "Generate my Travel Confidence Report"}
        </Button>
      </div>
    </AppShell>
  );
}
